import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import KeyboardArrowDownOutlined from "@mui/icons-material/KeyboardArrowDownOutlined";
import Spinner from "common/components/base/spinner/Spinner";
import useDelayedFlag from "common/hooks/useDelayedFlag";
import ReuseHarmonisationShimmer from "../../components/reuse-harmonisation-shimmer/ReuseHarmonisationShimmer";
import { ONTOLOGY_WORKBENCH_STEPS } from "../../data/create-ontology-questions.data";
import {
  DecisionBadge,
  DecisionTone,
  ReuseDecisionRow,
  ReuseHarmonisationSummary
} from "../../data/create-ontology-reuse-harmonisation.data";
import {
  OntologyService,
  ListPaginationMeta,
  OntologyReuseDecisionItemRequest
} from "../../data/ontology.service";
import "./CreateOntologyReuseHarmonisation.scss";

const EMPTY_REUSE_SUMMARY: ReuseHarmonisationSummary = {
  reuseExisting: 0,
  harmoniseExtend: 0,
  ontologiesReferenced: 0,
  conceptsAssessed: 0,
  ontologySourcesCount: 0
};

const EMPTY_REVIEWS_PAGINATION: ListPaginationMeta & {
  isLoadingMore: boolean;
} = {
  page: 1,
  totalPages: 1,
  totalRecords: 0,
  isLoadingMore: false
};

// Rows revealed at a time as the user scrolls, independent of how many the API fetched.
const REVIEWS_VISIBLE_CHUNK_SIZE = 10;
// Rows fetched per backend request; the next 100 are only fetched once the loaded rows run out.
const REVIEWS_PAGE_SIZE = 100;

// How often (and how long) to poll the initiative's currentStageName after triggering
// analysis, since generation runs asynchronously on the backend queue.
const REUSE_HARMONIZATION_POLL_INTERVAL_MS = 5000;
const REUSE_HARMONIZATION_POLL_TIMEOUT_MS = 120000;
const REUSE_HARMONIZATION_STAGE_IN_PROGRESS = "Reuse/Harmonization in progress";
const REUSE_HARMONIZATION_STAGE_COMPLETED = "Reuse/Harmonization Completed";
const REUSE_HARMONIZATION_STAGE_FAILED = "Reuse/Harmonization Failed";

// Backend stage names are free-text DB values, so compare case/whitespace-insensitively
// rather than requiring an exact match against the constants above.
const isStage = (stage: string | null, target: string): boolean =>
  (stage ?? "").trim().toLowerCase() === target.trim().toLowerCase();

const TONE_CLASS: Record<DecisionTone, string> = {
  green: "create-reuse-badge--green",
  blue: "create-reuse-badge--blue",
  orange: "create-reuse-badge--orange",
  red: "create-reuse-badge--red",
  grey: "create-reuse-badge--grey"
};

// Decisions a user can pick from the Decision Matrix's "User Decision" dropdown.
const USER_DECISION_OPTIONS = ["Reuse", "Harmonise", "Discard"];

// Mirrors the backend's reuse-review decision -> badge tone mapping, for optimistic UI
// updates when a decision is picked locally (before the bulk submit on Apply & Continue).
const USER_DECISION_TONE: Record<string, DecisionTone> = {
  Reuse: "green",
  Harmonise: "blue",
  Discard: "red"
};

// Maps a per-match AI recommendation label (matches.aiRecommendation) to a badge tone,
// since each candidate match can carry a different recommendation than the row overall.
const RECOMMENDATION_TONE: Record<string, DecisionTone> = {
  Reuse: "green",
  Harmonise: "blue",
  Extend: "orange",
  "Add to Initiative": "orange",
  Discard: "red"
};

// Truncates text to a max length with an ellipsis; the full text is still available via
// the native title tooltip on hover wherever this is used.
const truncateText = (text: string, maxLength: number): string =>
  text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;

// Rows without an explicit User Decision yet default to the AI Recommendation (when it's a
// selectable option), both so the dropdown isn't blank and so Apply & Continue submits a
// sensible decision even if the user never touches it. Returns the rows plus the staged
// decisions for any row that got defaulted this way.
const applyDefaultDecisions = (
  incomingRows: ReuseDecisionRow[]
): {
  rows: ReuseDecisionRow[];
  defaulted: Record<string, OntologyReuseDecisionItemRequest>;
} => {
  const defaulted: Record<string, OntologyReuseDecisionItemRequest> = {};
  const rows = incomingRows.map((row) => {
    if (
      row.currentDecision.label !== "Pending" ||
      !USER_DECISION_OPTIONS.includes(row.proposedDecision.label)
    ) {
      return row;
    }
    const label = row.proposedDecision.label;
    if (row.reviewId) {
      defaulted[row.reviewId] = {
        reviewId: row.reviewId,
        userDecision: label,
        matchId: row.selectedMatchId ?? undefined
      };
    }
    return {
      ...row,
      currentDecision: {
        label,
        tone: USER_DECISION_TONE[label] ?? row.proposedDecision.tone
      }
    };
  });
  return { rows, defaulted };
};

const ProposedBadge: React.FC<{ badge: DecisionBadge }> = ({ badge }) => (
  <span className={`create-reuse-badge ${TONE_CLASS[badge.tone]}`}>
    {badge.tone === "green" ? (
      <CheckOutlined className="create-reuse-badge__icon" />
    ) : (
      <ArrowForwardOutlined className="create-reuse-badge__icon" />
    )}
    {badge.label}
  </span>
);

interface SelectedMatchData {
  matchId: string | null;
  sourceOntology: string;
  coverage: number;
  recommendation: DecisionBadge;
}

// A row can have several candidate matches; the Source Ontology dropdown lets the user pick
// which one is active, and Coverage/AI Recommendation follow that pick. Defaults to the
// first match when none is explicitly selected yet. Rows without any matches (e.g. the
// static dummy data) fall back to the row's own top-level fields.
const getSelectedMatchData = (row: ReuseDecisionRow): SelectedMatchData => {
  const matches = row.matches ?? [];
  if (matches.length === 0) {
    return {
      matchId: null,
      sourceOntology: row.sourceOntology,
      coverage: row.confidencePercent,
      recommendation: row.proposedDecision
    };
  }
  const selected =
    matches.find((match) => match.matchId === row.selectedMatchId) ??
    matches[0];
  return {
    matchId: selected.matchId,
    sourceOntology: selected.sourceOntology,
    coverage: selected.coverage,
    recommendation: {
      label: selected.aiRecommendation,
      tone:
        RECOMMENDATION_TONE[selected.aiRecommendation] ??
        row.proposedDecision.tone
    }
  };
};

const CreateOntologyReuseHarmonisation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;
  // Set when navigated here right after triggering analysis, so we don't trigger it twice.
  const harmonizationTriggered = Boolean(
    (location.state as { harmonizationTriggered?: boolean } | null)
      ?.harmonizationTriggered
  );

  const [historyDrawerRow, setHistoryDrawerRow] =
    useState<ReuseDecisionRow | null>(null);
  const [rows, setRows] = useState<ReuseDecisionRow[]>([]);
  // How many of the loaded rows are currently rendered; grows by REVIEWS_VISIBLE_CHUNK_SIZE
  // as the user scrolls, independent of the larger API page size.
  const [visibleCount, setVisibleCount] = useState(REVIEWS_VISIBLE_CHUNK_SIZE);
  const [reviewsPagination, setReviewsPagination] = useState<
    ListPaginationMeta & { isLoadingMore: boolean }
  >(EMPTY_REVIEWS_PAGINATION);
  const [summary, setSummary] =
    useState<ReuseHarmonisationSummary>(EMPTY_REUSE_SUMMARY);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [isGeneratingReviews, setIsGeneratingReviews] = useState(false);
  // Only shows the shimmer once generation has been running for 2s, so a stage check that
  // resolves quickly (reviews already generated) never flashes it.
  const showReuseHarmonisationShimmer = useDelayedFlag(
    isGeneratingReviews,
    2000
  );
  // Latest polled currentStageName, so the loader can show a stage-specific message.
  const [harmonizationStage, setHarmonizationStage] = useState<string | null>(
    null
  );
  // The review id (or "bulk") currently being saved, so the relevant control can disable.
  const [savingDecisionFor, setSavingDecisionFor] = useState<string | null>(
    null
  );
  // User Decision dropdown picks staged locally (keyed by reviewId); only submitted as one
  // bulk request (POST .../reuse-reviews/decisions) when the user clicks Apply & Continue.
  const [pendingDecisions, setPendingDecisions] = useState<
    Record<string, OntologyReuseDecisionItemRequest>
  >({});

  const fetchReuseReviews = async () => {
    if (!initiativeId) return null;
    setIsLoadingReviews(true);
    try {
      const result = await OntologyService.getReuseReviews(
        initiativeId,
        1,
        REVIEWS_PAGE_SIZE
      );
      const { rows: defaultedRows, defaulted } = applyDefaultDecisions(
        result.rows
      );
      setRows(defaultedRows);
      setPendingDecisions((prev) => ({ ...prev, ...defaulted }));
      setSummary(result.summary);
      setVisibleCount(Math.min(REVIEWS_VISIBLE_CHUNK_SIZE, result.rows.length));
      setReviewsPagination({
        page: result.page,
        totalPages: result.totalPages,
        totalRecords: result.totalRecords,
        isLoadingMore: false
      });
      return result;
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // Fetches the next 100 rows from the API once the currently loaded rows are exhausted.
  const loadMoreReuseReviews = async () => {
    if (!initiativeId) return;
    if (reviewsPagination.isLoadingMore) return;
    if (reviewsPagination.page >= reviewsPagination.totalPages) return;
    setReviewsPagination((prev) => ({ ...prev, isLoadingMore: true }));
    const result = await OntologyService.getReuseReviews(
      initiativeId,
      reviewsPagination.page + 1,
      REVIEWS_PAGE_SIZE
    );
    const { rows: defaultedRows, defaulted } = applyDefaultDecisions(
      result.rows
    );
    setRows((prev) => [...prev, ...defaultedRows]);
    setPendingDecisions((prev) => ({ ...prev, ...defaulted }));
    setVisibleCount((prev) => prev + REVIEWS_VISIBLE_CHUNK_SIZE);
    setReviewsPagination({
      page: result.page,
      totalPages: result.totalPages,
      totalRecords: result.totalRecords,
      isLoadingMore: false
    });
  };

  // Reveals the next chunk of already-loaded rows, or fetches the next API page once those
  // run out, each time the matrix is scrolled near its bottom.
  const handleMatrixScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight >= 80) return;
    if (visibleCount < rows.length) {
      setVisibleCount((prev) =>
        Math.min(prev + REVIEWS_VISIBLE_CHUNK_SIZE, rows.length)
      );
    } else {
      loadMoreReuseReviews();
    }
  };

  // Polls the initiative's currentStageName until harmonization reaches a terminal stage:
  // Completed fetches the reviews, Failed sends the user back to Entity Generation, and
  // in-progress keeps the shimmer showing while polling continues.
  useEffect(() => {
    if (!initiativeId) return;
    let isMounted = true;
    (async () => {
      setIsGeneratingReviews(true);
      let initiative = await OntologyService.getInitiativeById(initiativeId);
      if (!isMounted) return;
      setHarmonizationStage(initiative.currentStageName);
      if (
        !harmonizationTriggered &&
        !isStage(
          initiative.currentStageName,
          REUSE_HARMONIZATION_STAGE_IN_PROGRESS
        ) &&
        !isStage(
          initiative.currentStageName,
          REUSE_HARMONIZATION_STAGE_COMPLETED
        ) &&
        !isStage(initiative.currentStageName, REUSE_HARMONIZATION_STAGE_FAILED)
      ) {
        await OntologyService.triggerReuseHarmonizeAnalysis(initiativeId);
        // The trigger flips the backend stage to in-progress synchronously, so reflect that
        // immediately rather than showing the generic message until the first poll lands.
        initiative = {
          ...initiative,
          currentStageName: REUSE_HARMONIZATION_STAGE_IN_PROGRESS
        };
        if (isMounted) setHarmonizationStage(initiative.currentStageName);
      }
      const deadline = Date.now() + REUSE_HARMONIZATION_POLL_TIMEOUT_MS;
      while (isMounted && Date.now() < deadline) {
        if (
          isStage(initiative.currentStageName, REUSE_HARMONIZATION_STAGE_FAILED)
        ) {
          navigate("/ontology-studio/create/entities-relationships", {
            state: { initiativeId }
          });
          return;
        }
        if (
          isStage(
            initiative.currentStageName,
            REUSE_HARMONIZATION_STAGE_COMPLETED
          )
        ) {
          await fetchReuseReviews();
          setIsGeneratingReviews(false);
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, REUSE_HARMONIZATION_POLL_INTERVAL_MS)
        );
        if (!isMounted) return;
        initiative = await OntologyService.getInitiativeById(initiativeId);
        if (isMounted) setHarmonizationStage(initiative.currentStageName);
      }
      if (isMounted) setIsGeneratingReviews(false);
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiativeId]);

  // Opens the history drawer and always re-fetches each candidate match's rationale, so the
  // summary shown reflects the latest reasoning rather than whatever the reviews list cached.
  const handleViewHistory = async (row: ReuseDecisionRow) => {
    setHistoryDrawerRow(row);
    if (!initiativeId) return;
    const matches = row.matches ?? [];
    if (matches.length === 0) return;
    const results = await Promise.all(
      matches.map((match) =>
        OntologyService.getReuseMatchRationale(initiativeId, match.matchId)
      )
    );
    setHistoryDrawerRow((prev) => {
      if (!prev || prev.id !== row.id) return prev;
      return {
        ...prev,
        matches: (prev.matches ?? []).map((match, index) => ({
          ...match,
          rationale: results[index] ?? match.rationale
        }))
      };
    });
  };

  // Stages the User Decision dropdown's chosen value locally and updates the badge
  // optimistically; the actual save is a single bulk request fired from Apply & Continue.
  const handleDecisionChange = (
    row: ReuseDecisionRow,
    userDecision: string
  ) => {
    if (!row.reviewId || !userDecision) return;
    setPendingDecisions((prev) => ({
      ...prev,
      [row.reviewId as string]: {
        reviewId: row.reviewId as string,
        userDecision,
        matchId: row.selectedMatchId ?? undefined
      }
    }));
    setRows((prev) =>
      prev.map((item) =>
        item.id === row.id
          ? {
              ...item,
              currentDecision: {
                label: userDecision,
                tone: USER_DECISION_TONE[userDecision] ?? "grey"
              }
            }
          : item
      )
    );
  };

  // Picks which candidate match is active for a row; Coverage and AI Recommendation follow
  // this selection, and it's carried as matchId the next time a User Decision is saved.
  const handleSourceOntologyChange = (
    row: ReuseDecisionRow,
    matchId: string
  ) => {
    setRows((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, selectedMatchId: matchId } : item
      )
    );
    if (row.reviewId && pendingDecisions[row.reviewId]) {
      setPendingDecisions((prev) => ({
        ...prev,
        [row.reviewId as string]: { ...prev[row.reviewId as string], matchId }
      }));
    }
  };

  // Auto-accepts the AI recommendation for every row that doesn't have a decision yet.
  const handleAcceptAllRecommendations = async () => {
    if (!initiativeId) return;
    setSavingDecisionFor("bulk");
    try {
      await OntologyService.setReuseDecisionsBulk(initiativeId, [], true);
      await fetchReuseReviews();
    } finally {
      setSavingDecisionFor(null);
    }
  };

  const handleBack = () => {
    navigate("/ontology-studio/create/entities-relationships", {
      state: { initiativeId }
    });
  };

  const handleContinue = async () => {
    const decisions = Object.values(pendingDecisions);
    if (initiativeId && decisions.length > 0) {
      setSavingDecisionFor("continue");
      try {
        await OntologyService.setReuseDecisionsBulk(
          initiativeId,
          decisions,
          false
        );
      } finally {
        setSavingDecisionFor(null);
      }
    }
    navigate("/ontology-studio/create/ground-to-documents", {
      state: { initiativeId }
    });
  };

  // Rows (loaded so far) that still have no explicit User Decision; the backend's summary
  // counts are bucketed by AI Recommendation, not by user decision, so they can't be used
  // here. Apply & Continue stays disabled until this reaches zero.
  const pendingDecisionsCount = rows.filter(
    (row) => row.currentDecision.label === "Pending"
  ).length;

  return (
    <div className="create-reuse-container">
      <nav className="create-reuse-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ontology Discovery and Reuse</span>
      </nav>

      <div className="create-reuse-header">
        <h1 className="create-reuse-title">
          Reuse &amp; Harmonisation Decisions
        </h1>
        <p className="create-reuse-description">
          Review reuse, harmonisation, extension, and add-to-initiative
          decisions for this lifecycle stage. Focus on the final alignment of
          concepts before continuing to Ground to Documents.
        </p>
        <div className="create-reuse-meta">
          <span className="create-reuse-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-reuse-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-reuse-workflow">
        <div className="create-reuse-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(0, 3).map((step) => (
            <div
              key={step.id}
              className="create-reuse-workflow__step create-reuse-workflow__step--done"
            >
              <span className="create-reuse-workflow__indicator">✓</span>
              <span className="create-reuse-workflow__label">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="create-reuse-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(3, 6).map((step) => (
            <div
              key={step.id}
              className={`create-reuse-workflow__step${
                step.id === 4 ? " create-reuse-workflow__step--active" : ""
              }`}
            >
              <span className="create-reuse-workflow__indicator">
                {step.id === 4 ? "•" : ""}
              </span>
              <span className="create-reuse-workflow__label">{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="create-reuse-summary">
        <div className="create-reuse-summary__header">
          <h3 className="create-reuse-summary__title">Decision Summary</h3>
          <span className="create-reuse-summary__meta">
            {summary.conceptsAssessed} concepts assessed ·{" "}
            {summary.ontologySourcesCount} ontology sources
          </span>
        </div>
        <div className="create-reuse-summary__row">
          <div className="create-reuse-summary-card create-reuse-summary-card--green">
            <span className="create-reuse-summary-card__label">
              Reuse Existing
            </span>
            <span className="create-reuse-summary-card__value">
              {summary.reuseExisting}
            </span>
            <span className="create-reuse-summary-card__caption">
              Concepts aligned to existing ontology
            </span>
          </div>
          <div className="create-reuse-summary-card create-reuse-summary-card--blue">
            <span className="create-reuse-summary-card__label">
              Harmonise &amp; Extend
            </span>
            <span className="create-reuse-summary-card__value">
              {summary.harmoniseExtend}
            </span>
            <span className="create-reuse-summary-card__caption">
              Concepts requiring mapping alignment
            </span>
          </div>
          <div className="create-reuse-summary-card create-reuse-summary-card--orange">
            <span className="create-reuse-summary-card__label">
              Ontologies referenced
            </span>
            <span className="create-reuse-summary-card__value">
              {summary.ontologiesReferenced}
            </span>
            <span className="create-reuse-summary-card__caption">
              Across all ontologies
            </span>
          </div>
          <div className="create-reuse-summary-card">
            <span className="create-reuse-summary-card__label">
              Concepts Assessed
            </span>
            <span className="create-reuse-summary-card__value">
              {summary.conceptsAssessed}
            </span>
            <span className="create-reuse-summary-card__caption">
              Across all decision groups
            </span>
          </div>
        </div>
      </div>

      <div className="create-reuse-matrix">
        <div className="create-reuse-matrix__header">
          <div className="create-reuse-matrix__title-block">
            <h3 className="create-reuse-matrix__title">Decision Matrix</h3>
            <p className="create-reuse-matrix__subtitle">
              Compare concepts, sources, confidence, and proposed decisions in
              one view.
            </p>
          </div>
          <div className="create-reuse-matrix__filter">
            <SearchOutlined fontSize="small" />
            <span>Filter concepts...</span>
          </div>
          {/* <button
            type="button"
            className="create-reuse-btn create-reuse-btn--secondary"
            disabled={savingDecisionFor === "bulk" || rows.length === 0}
            onClick={handleAcceptAllRecommendations}
          >
            {savingDecisionFor === "bulk" ? "Applying…" : "Accept All AI Recommendations"}
          </button> */}
        </div>

        {isLoadingReviews && rows.length === 0 ? (
          <div className="create-reuse-table-wrapper create-reuse-table-wrapper--empty">
            <Spinner size={20} />
          </div>
        ) : isGeneratingReviews ? (
          showReuseHarmonisationShimmer ? (
            <ReuseHarmonisationShimmer
              statusMessage={
                isStage(
                  harmonizationStage,
                  REUSE_HARMONIZATION_STAGE_IN_PROGRESS
                )
                  ? "Reuse/Harmonization in progress please wait for sometime"
                  : undefined
              }
            />
          ) : (
            <div className="create-reuse-table-wrapper create-reuse-table-wrapper--empty">
              <Spinner size={20} />
            </div>
          )
        ) : rows.length === 0 ? (
          <div className="create-reuse-table-wrapper create-reuse-table-wrapper--empty">
            No reuse/harmonisation reviews available yet.
          </div>
        ) : (
          <div className="create-reuse-table-wrapper">
            <div
              className="create-reuse-table-scroll"
              onScroll={handleMatrixScroll}
            >
              <table className="create-reuse-table">
                <thead>
                  <tr>
                    <th className="create-reuse-table__col-concept">Concept</th>
                    <th className="create-reuse-table__col-source">
                      Source Ontology
                    </th>
                    <th className="create-reuse-table__col-confidence">
                      Coverage
                    </th>
                    <th className="create-reuse-table__col-decision">
                      AI Recommendation
                    </th>
                    <th className="create-reuse-table__col-decision">
                      User Decision
                    </th>
                    <th className="create-reuse-table__col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, visibleCount).map((row) => {
                    const matches = row.matches ?? [];
                    const selectedMatch = getSelectedMatchData(row);
                    return (
                      <tr
                        key={row.id}
                        className={
                          historyDrawerRow?.id === row.id
                            ? "create-reuse-table__row--highlight"
                            : undefined
                        }
                      >
                        <td className="create-reuse-table__col-concept">
                          <span
                            className="create-reuse-table__concept-name"
                            title={row.conceptName}
                          >
                            {truncateText(row.conceptName, 20)}
                          </span>
                          <span className="create-reuse-table__concept-category">
                            {row.conceptCategory}
                          </span>
                        </td>
                        <td className="create-reuse-table__col-source">
                          {matches.length > 1 ? (
                            <div className="create-reuse-decision-select create-reuse-decision-select--source">
                              <select
                                className="create-reuse-decision-select__control"
                                value={selectedMatch.matchId ?? ""}
                                onChange={(event) =>
                                  handleSourceOntologyChange(
                                    row,
                                    event.target.value
                                  )
                                }
                              >
                                {matches.map((match) => (
                                  <option
                                    key={match.matchId}
                                    value={match.matchId}
                                    title={match.sourceOntology}
                                  >
                                    {truncateText(match.sourceOntology, 40)}
                                  </option>
                                ))}
                              </select>
                              <KeyboardArrowDownOutlined className="create-reuse-decision-select__chevron" />
                            </div>
                          ) : (
                            <span title={selectedMatch.sourceOntology}>
                              {truncateText(selectedMatch.sourceOntology, 80)}
                            </span>
                          )}
                        </td>
                        <td className="create-reuse-table__col-confidence">
                          <span className="create-reuse-table__confidence-value">
                            {Math.round(selectedMatch.coverage)}%
                          </span>
                        </td>
                        <td className="create-reuse-table__col-decision">
                          <ProposedBadge badge={selectedMatch.recommendation} />
                        </td>
                        <td className="create-reuse-table__col-decision">
                          <div className="create-reuse-decision-select">
                            <select
                              className="create-reuse-decision-select__control"
                              value={
                                USER_DECISION_OPTIONS.includes(
                                  row.currentDecision.label
                                )
                                  ? row.currentDecision.label
                                  : ""
                              }
                              disabled={
                                !row.reviewId ||
                                savingDecisionFor === "continue"
                              }
                              onChange={(event) =>
                                handleDecisionChange(row, event.target.value)
                              }
                            >
                              <option value="" disabled>
                                Select decision
                              </option>
                              {USER_DECISION_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                            <KeyboardArrowDownOutlined className="create-reuse-decision-select__chevron" />
                          </div>
                        </td>
                        <td className="create-reuse-table__col-actions">
                          <button
                            type="button"
                            className="create-reuse-action-btn"
                            onClick={() => handleViewHistory(row)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {reviewsPagination.isLoadingMore && (
              <p className="create-reuse-table-wrapper__loading-more">
                Loading more…
              </p>
            )}
          </div>
        )}

        <div className="create-reuse-legend">
          <span className="create-reuse-legend__label">
            SYS. RECOMMENDATION KEY:
          </span>
          <span className="create-reuse-legend__item">
            <span className="create-reuse-legend__swatch create-reuse-legend__swatch--green" />
            Reuse - adopt with alignment to initiative scope
          </span>
          <span className="create-reuse-legend__item">
            <span className="create-reuse-legend__swatch create-reuse-legend__swatch--blue" />
            Harmonise - map or extend to align
          </span>
          <span className="create-reuse-legend__item">
            <span className="create-reuse-legend__swatch create-reuse-legend__swatch--red" />
            Add to Initiative - create for this initiative
          </span>
        </div>
      </div>

      <div className="create-reuse-footer">
        <button
          type="button"
          className="create-reuse-btn create-reuse-btn--primary"
          disabled={
            savingDecisionFor === "continue" ||
            pendingDecisionsCount > 0 ||
            isStage(harmonizationStage, REUSE_HARMONIZATION_STAGE_IN_PROGRESS)
          }
          title={
            isStage(harmonizationStage, REUSE_HARMONIZATION_STAGE_IN_PROGRESS)
              ? "Reuse/Harmonization in progress please wait for sometime"
              : pendingDecisionsCount > 0
              ? `${pendingDecisionsCount} user decision${
                  pendingDecisionsCount === 1 ? "" : "s"
                } pending`
              : undefined
          }
          onClick={handleContinue}
        >
          {savingDecisionFor === "continue" ? (
            "Applying…"
          ) : (
            <>Apply &amp; Continue →</>
          )}
        </button>
      </div>

      {historyDrawerRow && (
        <div className="create-reuse-drawer-root">
          <div
            className="create-reuse-drawer-overlay"
            onClick={() => setHistoryDrawerRow(null)}
          />
          <aside className="create-reuse-drawer">
            <div className="create-reuse-drawer__header">
              <div className="create-reuse-drawer__title-row">
                <span className="create-reuse-drawer__icon">
                  <AutoAwesomeOutlined fontSize="small" />
                </span>
                <div className="create-reuse-drawer__title-group">
                  <h2 className="create-reuse-drawer__title">AI Summary</h2>
                  <p className="create-reuse-drawer__subtitle">
                    Harmonisation Reasoning
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="create-reuse-drawer__close"
                aria-label="Close"
                onClick={() => setHistoryDrawerRow(null)}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-reuse-drawer__body">
              <p className="create-reuse-drawer__description">
                {`AI-recommended candidate matches for "${historyDrawerRow.conceptName}", with the rationale behind each recommendation.`}
              </p>

              <div className="create-reuse-drawer__reasoning-list">
                {(historyDrawerRow.matches ?? []).length === 0 ? (
                  <p className="create-reuse-drawer__reasoning-text">
                    No candidate matches recorded for this concept.
                  </p>
                ) : (
                  (historyDrawerRow.matches ?? []).map((match) => (
                    <div
                      key={match.matchId}
                      className="create-reuse-drawer__reasoning-block"
                    >
                      <p className="create-reuse-drawer__reasoning-title">
                        {match.sourceNodeName} ({match.sourceOntology})
                      </p>
                      <p className="create-reuse-drawer__reasoning-text">
                        {match.rationale ?? "Loading rationale…"}
                      </p>
                      <p className="create-reuse-drawer__reasoning-confidence">
                        AI recommendation: {match.aiRecommendation} ·
                        Confidence: {Math.round(match.coverage)}%
                      </p>
                    </div>
                  ))
                )}
              </div>

              <p className="create-reuse-drawer__disclaimer">
                AI recommendations are based on semantic analysis and should be
                reviewed before applying.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CreateOntologyReuseHarmonisation;
